import axios from 'axios';
import type {
  ApiSuccess,
  ApplyCodeResult,
  AttributionTouchInput,
  AttributionTouchResult,
  CampusPassStartInput,
  CampusPhotoStartInput,
  CampusPhotoTicket,
  MyCampusPass,
  MyCashback,
  MyReferral,
  OrderCashback,
} from '@capsico/contracts';
import { api } from './client';

/* W18 growth: refer a friend, codes, attribution touches, Campus Pass, cashback. */

const BASE = '/v1/customer/me/growth';

export async function fetchReferral(): Promise<MyReferral> {
  const res = await api.get<ApiSuccess<MyReferral>>(`${BASE}/referral`);
  return res.data.data;
}

export async function applyGrowthCode(code: string): Promise<ApplyCodeResult> {
  const res = await api.post<ApiSuccess<ApplyCodeResult>>(`${BASE}/codes`, { code });
  return res.data.data;
}

export async function sendTouch(input: AttributionTouchInput): Promise<AttributionTouchResult> {
  const res = await api.post<ApiSuccess<AttributionTouchResult>>(`${BASE}/touch`, input);
  return res.data.data;
}

export async function fetchCampus(
  near: { lat: number; lng: number } | null,
): Promise<MyCampusPass> {
  const res = await api.get<ApiSuccess<MyCampusPass>>(`${BASE}/campus`, {
    params: near ?? {},
  });
  return res.data.data;
}

export async function startCampusPass(input: CampusPassStartInput): Promise<MyCampusPass> {
  const res = await api.post<ApiSuccess<MyCampusPass>>(`${BASE}/campus/pass`, input);
  return res.data.data;
}

/** The 6-digit college email code. It is sent once and never stored on the phone. */
export async function verifyCampusEmail(code: string): Promise<MyCampusPass> {
  const res = await api.post<ApiSuccess<MyCampusPass>>(`${BASE}/campus/pass/verify-email`, {
    code,
  });
  return res.data.data;
}

/** ID photo: ask for a short-lived upload link, PUT the file straight to storage, then confirm. */
export async function uploadCampusPhoto(
  input: CampusPhotoStartInput,
  file: Blob,
  onProgress?: (percent: number) => void,
): Promise<MyCampusPass> {
  const ticket = await api.post<ApiSuccess<CampusPhotoTicket>>(`${BASE}/campus/pass/photo`, input);
  const { uploadUrl, headers } = ticket.data.data;
  // Plain axios (not the API client): the upload goes to storage, not to the Capsico API.
  await axios.put(uploadUrl, file, {
    headers,
    timeout: 120_000,
    onUploadProgress: (event) => {
      if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
    },
  });
  const done = await api.post<ApiSuccess<MyCampusPass>>(`${BASE}/campus/pass/photo/complete`);
  return done.data.data;
}

export async function fetchCashback(): Promise<MyCashback> {
  const res = await api.get<ApiSuccess<MyCashback>>(`${BASE}/cashback`);
  return res.data.data;
}

export async function fetchOrderCashback(orderId: string): Promise<OrderCashback> {
  const res = await api.get<ApiSuccess<OrderCashback>>(`${BASE}/cashback/orders/${orderId}`);
  return res.data.data;
}
